import { Component, OnInit, Renderer2, ViewEncapsulation } from '@angular/core';
import { GlobalStateProviderService } from '../../services/global-state-provider.service';
import { NavigationEnd, Router } from '@angular/router';

@Component({
  selector: 'app-configurator',
  templateUrl: './configurator.component.html',
  styleUrls: ['./configurator.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ConfiguratorComponent implements OnInit {
  activeTab: string = 'system';
  defaultOpenSections: boolean = false;
  searchTermText: string = '';
  searchTerm: string = '';
  searchTermWords: string[] = [];
  advancedFlag: boolean;

  searchKeywordsMap: any = {};
  searchResultMap: any = {};

  routeVisibilityConditionMap: any = {};
  routeVisibilityMap: any = {};

  constructor(
    public globalStateProviderService: GlobalStateProviderService,
    private readonly renderer: Renderer2,
    private readonly router: Router
  ) {
    router.events.subscribe((e) => {
      if (e instanceof NavigationEnd) {
        this.routeVisibilityMap =
          this.checkRouteVisibility(this.routeVisibilityConditionMap) || {};
        this.checkActiveTab();
      }
    });
    this.advancedFlag = false;
  }

  ngOnInit(): void {
    this.initSearchKeywordsMap();
    this.initRouteVisibilityConditionMap();
  }

  initSearchKeywordsMap(): void {
    const fontSizeKeywords = [
      'text',
      'size',
      'fonts',
      'ds-config-label-text-size'
    ];
    const fontWeightKeywords = [
      'text',
      'weight',
      'fonts',
      'ds-config-label-font-weight'
    ];
    const fontcolorKeywords = [
      'text',
      'colors',
      'fonts',
      'ds-config-label-text-color'
    ];
    const bgColorKeywords = [
      'colors',
      'background',
      'bg',
      'ds-config-label-background-color'
    ];
    const borderRadiusKeywords = [
      'corners',
      'borders',
      'radius',
      'roundness',
      'ds-config-label-corner-roundness'
    ];
    const horizontalPaddingKeywords = [
      'horizontal',
      'padding',
      'inline',
      'space',
      'distance',
      'x',
      'ds-config-label-horizontal-padding'
    ];
    const verticalPaddingKeywords = [
      'vertical',
      'padding',
      'block',
      'space',
      'distance',
      'y',
      'ds-config-label-vertical-padding'
    ];
    const prefixSuffixGlobals = [
      'prefix',
      'suffix',
      'ds-config-subsection-prefix-suffix'
    ];
    this.searchKeywordsMap = {
      system: {
        general: {
          subsection1: {
            column1: {
              primaryColor: [
                'primary',
                'color',
                'main',
                'ds-config-label-primary-color'
              ],
              systemBackgroundColor: ['page', ...bgColorKeywords]
            },
            darkMode: [
              'night',
              'dark',
              'mode',
              'color',
              'black',
              'ds-config-label-dark-mode'
            ]
          },
          colors: {
            defaultColor: [
              'system',
              'color',
              'default',
              'draft',
              'ds-config-label-default-color'
            ],
            infoColor: [
              'system',
              'color',
              'info',
              'blue',
              'pending',
              'ds-config-label-info-color'
            ],
            successColor: [
              'system',
              'color',
              'success',
              'green',
              'processed',
              'ds-config-label-success-color'
            ],
            warningColor: [
              'system',
              'color',
              'warning',
              'orange',
              'correction',
              'ds-config-label-warning-color'
            ],
            dangerColor: [
              'system',
              'color',
              'danger',
              'error',
              'red',
              'rejected',
              'ds-config-label-danger-color'
            ]
          },
          fonts: {
            fontFamily: [
              'base',
              'fonts',
              'family',
              'typography',
              'ds-config-label-base-font-family'
            ],
            fontSize: [
              'base',
              'fonts',
              'size',
              'typography',
              'ds-config-label-base-font-size'
            ],
            paragraphFontSize: [
              'base',
              'fonts',
              'paragraphs',
              'size',
              'typography',
              'ds-config-label-paragraph-font-size'
            ]
          },
          fontWeights: {
            column1: {
              light: [
                'fonts',
                'weights',
                'light',
                'typography',
                'ds-config-font-weight-light'
              ],
              regular: [
                'fonts',
                'weights',
                'regular',
                'typography',
                'ds-config-font-weight-regular'
              ],
              semibold: [
                'fonts',
                'weights',
                'semibold',
                'typography',
                'ds-config-font-weight-semibold'
              ]
            },
            column2: {
              bold: [
                'fonts',
                'weights',
                'bold',
                'typography',
                'ds-config-font-weight-bold'
              ],
              black: [
                'fonts',
                'weights',
                'black',
                'typography',
                'ds-config-font-weight-black'
              ]
            }
          },
          spacing: {
            baseSpace: ['spaces', 'spacing', 'base', 'ds-config-base-space'],
            mainAreaSpace: [
              'spaces',
              'spacing',
              'main',
              'areas',
              'ds-config-main-areas-space'
            ]
          },
          scrollbar: {
            trackColor: [
              'track',
              'colors',
              'scrollbar',
              'ds-config-label-scroll-track-color'
            ],
            thumbColor: [
              'thumb',
              'colors',
              'scrollbar',
              'ds-config-label-scroll-thumb-color'
            ]
          },
          others: {
            cornerRoundness: ['others', ...borderRadiusKeywords]
          }
        },
        pageHeader: {
          subsection1: {
            logoMaxHeight: [
              'logo',
              'height',
              'header',
              'image',
              'ds-config-label-logo-height'
            ],
            backgroundColor: ['header', 'page', ...bgColorKeywords]
          },
          logoutLink: {
            column1: {
              textSize: ['logout', 'link', 'button', ...fontSizeKeywords],
              fontWeight: ['logout', 'link', 'button', ...fontWeightKeywords],
              textColor: ['logout', 'link', 'button', ...fontcolorKeywords]
            },
            column2: {
              gap: [
                'logout',
                'link',
                'button',
                'space',
                'separation',
                'gap',
                'ds-config-label-gap'
              ],
              showIcon: [
                'logout',
                'link',
                'button',
                'icon',
                'show',
                'toggle',
                'ds-config-label-show-icon'
              ]
            }
          }
        },
        menuBar: {
          subsection1: {
            menuBarHeight: [
              'menu',
              'bar',
              'height',
              'top',
              'main',
              'page',
              'ds-config-label-menu-bar-height'
            ],
            showHomeIcon: [
              'menu',
              'bar',
              'home',
              'house',
              'main',
              'page',
              'ds-config-show-home-icon'
            ]
          },
          breadcrumbs: {
            column1: {
              textSize: ['breadcrumbs', 'service', 'name', ...fontSizeKeywords],
              fontWeight: [
                'breadcrumbs',
                'service',
                'name',
                ...fontWeightKeywords
              ],
              textColor: [
                'breadcrumbs',
                'service',
                'name',
                ...fontcolorKeywords
              ]
            },
            separatorSize: [
              'breadcrumbs',
              'separator',
              'divider',
              'angle',
              'arrow',
              'size',
              'ds-config-label-separator-size'
            ]
          },
          menu: {
            textSize: ['menu', 'user', 'lang', 'main', ...fontSizeKeywords],
            fontWeight: ['menu', 'user', 'lang', 'main', ...fontWeightKeywords],
            textColor: ['menu', 'user', 'lang', 'main', ...fontcolorKeywords]
          },
          dropdownMenu: {
            column1: {
              showIcons: [
                'show',
                'icons',
                'dropdown',
                'menu',
                'ds-config-label-show-icons'
              ],
              iconsColor: [
                'colors',
                'icons',
                'dropdown',
                'menu',
                'ds-config-label-icons-color'
              ]
            },
            column2: {
              itemHeight: [
                'menu',
                'item',
                'link',
                'dropdown',
                'height',
                'ds-config-label-item-height'
              ],
              dividerHeight: [
                'menu',
                'item',
                'dropdown',
                'height',
                'divider',
                'separator',
                'line',
                'ds-config-label-divider-height'
              ]
            },
            column3: {
              textSize: [
                'menu',
                'user',
                'dropdown',
                'main',
                'links',
                'pages',
                ...fontSizeKeywords
              ],
              textColor: [
                'menu',
                'user',
                'dropdown',
                'main',
                'links',
                'pages',
                ...fontcolorKeywords
              ]
            }
          }
        },
        dashboard: {
          tabs: {
            column1: {
              textSize: ['dashboard', 'tabs', 'pill', ...fontSizeKeywords],
              cornerRoundness: ['dashboard', 'tabs', ...borderRadiusKeywords]
            },
            column2: {
              tabPillYPadding: [
                'dashboard',
                'tabs',
                'pill',
                'ds-config-label-tab-pill-vertical-padding',
                ...verticalPaddingKeywords
              ],
              contentPadding: [
                'dashboard',
                'tabs',
                'content',
                'padding',
                'spaces',
                'distance',
                'ds-config-label-content-padding'
              ]
            }
          },
          listsHeading: {
            column1: {
              textSize: [
                'dashboard',
                'lists',
                'headings',
                'files',
                'tables',
                ...fontSizeKeywords
              ],
              fontWeight: [
                'dashboard',
                'lists',
                'headings',
                'files',
                'tables',
                ...fontWeightKeywords
              ]
            },
            column2: {
              textColor: [
                'dashboard',
                'lists',
                'headings',
                'files',
                'tables',
                ...fontcolorKeywords
              ],
              backgroundColor: [
                'dashboard',
                'lists',
                'headings',
                'files',
                'tables',
                ...bgColorKeywords
              ]
            }
          },
          listsRows: {
            textSize: [
              'dashboard',
              'lists',
              'rows',
              'files',
              'tables',
              ...fontSizeKeywords
            ],
            fontWeight: [
              'dashboard',
              'lists',
              'rows',
              'files',
              'tables',
              ...fontWeightKeywords
            ]
          },
          listsPagination: {
            column1: {
              height: [
                'dashboard',
                'lists',
                'pagination',
                'pages',
                'paging',
                'height',
                'ds-config-label-height'
              ],
              textSize: [
                'dashboard',
                'lists',
                'pagination',
                'pages',
                'paging',
                ...fontSizeKeywords
              ],
              fontWeight: [
                'dashboard',
                'lists',
                'pagination',
                'pages',
                'paging',
                ...fontWeightKeywords
              ]
            },
            column2: {
              topSpace: [
                'dashboard',
                'lists',
                'pagination',
                'pages',
                'paging',
                'top',
                'space',
                'ds-config-label-pagination-top-space'
              ]
            }
          },
          statusLegend: {
            topSpace: [
              'dashboard',
              'lists',
              'status',
              'legend',
              'filter',
              'top',
              'space',
              'ds-config-label-status-legend-filter-top-space'
            ],
            textSize: [
              'dashboard',
              'lists',
              'status',
              'legend',
              'filter',
              ...fontSizeKeywords
            ]
          }
        },
        availableServices: {
          sectionTitleSearchGap: [
            'available',
            'services',
            'section',
            'title',
            'search',
            'gap',
            'ds-config-label-section-title-search-gap'
          ],
          cards: {
            column1: {
              roundnessCorners: [
                'available',
                'services',
                'card',
                ...borderRadiusKeywords,
                'ds-config-label-border-roundness'
              ],
              gap: [
                'available',
                'services',
                'card',
                'gap',
                'distance',
                'space',
                'ds-config-label-gap'
              ]
            },
            column2: {
              showServiceImage: [
                'available',
                'services',
                'card',
                'image',
                'logo',
                'ds-config-label-show-service-image'
              ],
              initialShadow: [
                'available',
                'services',
                'card',
                'shadow',
                'initial',
                'ds-config-label-initial-shadow'
              ],
              mouseoverShadow: [
                'available',
                'services',
                'card',
                'shadow',
                'mouseover',
                'hover',
                'ds-config-label-mouseover-shadow'
              ]
            }
          },
          cardTitle: {
            textSize: [
              'available',
              'services',
              'card',
              'title',
              ...fontSizeKeywords
            ],
            fontWeight: [
              'available',
              'services',
              'card',
              'title',
              ...fontWeightKeywords
            ],
            textColor: [
              'available',
              'services',
              'card',
              'title',
              ...fontcolorKeywords
            ]
          },
          cardDescription: {
            textSize: [
              'available',
              'services',
              'card',
              'description',
              ...fontSizeKeywords
            ],
            fontWeight: [
              'available',
              'services',
              'card',
              'description',
              ...fontWeightKeywords
            ],
            textColor: [
              'available',
              'services',
              'card',
              'description',
              ...fontcolorKeywords
            ]
          }
        },
        footer: {
          textSize: [
            'footer',
            'page',
            'main',
            'copyright',
            ...fontSizeKeywords
          ],
          fontWeight: [
            'footer',
            'page',
            'main',
            'copyright',
            ...fontWeightKeywords
          ],
          textColor: [
            'footer',
            'page',
            'main',
            'copyright',
            ...fontcolorKeywords
          ]
        }
      },
      forms: {
        general: {
          componentSeparation: [
            'space',
            'separation',
            'component',
            'distance',
            'ds-config-label-component-separation'
          ]
        },
        wizard: {
          gradientTopColor: [
            'gradient',
            'top',
            ...bgColorKeywords,
            'ds-config-label-gradient-top-color'
          ],
          pageLink: {
            column1: {
              textSize: ['page', 'link', 'wizard', 'tab', ...fontSizeKeywords],
              fontWeight: [
                'page',
                'link',
                'wizard',
                'tab',
                ...fontWeightKeywords
              ]
            },
            column2: {
              horizontalPadding: [
                'page',
                'link',
                'wizard',
                'tab',
                ...horizontalPaddingKeywords
              ],
              verticalPadding: [
                'page',
                'link',
                'wizard',
                'tab',
                ...verticalPaddingKeywords
              ],
              pillWidth: ['page', 'link', 'wizard', 'tab', 'width']
            }
          },
          progressBar: {
            height: [
              'page',
              'link',
              'wizard',
              'tab',
              'progress',
              'bar',
              'ds-config-label-height'
            ],
            bottomDistance: [
              'page',
              'link',
              'wizard',
              'tab',
              'progress',
              'bar',
              'ds-config-label-bottom-distance'
            ]
          }
        },
        labels: {
          subsection1: {
            column1: {
              textSize: ['labels', 'forms', 'components', ...fontSizeKeywords],
              fontWeight: [
                'labels',
                'forms',
                'components',
                ...fontWeightKeywords
              ],
              lineHeight: [
                'labels',
                'forms',
                'components',
                'line',
                'height',
                'ds-config-label-line-height'
              ]
            },
            verticalPadding: [
              'labels',
              'forms',
              'components',
              ...verticalPaddingKeywords
            ]
          },
          requiredMark: {
            symbol: [
              'labels',
              'forms',
              'components',
              'required',
              'mark',
              'symbol',
              'ds-config-label-required-mark-symbol'
            ],
            size: [
              'labels',
              'forms',
              'components',
              'required',
              'mark',
              'size',
              'ds-config-label-required-mark-size'
            ],
            color: [
              'labels',
              'forms',
              'components',
              'required',
              'mark',
              'color',
              'ds-config-label-color'
            ]
          }
        },
        fields: {
          subsection1: {
            height: [
              'fields',
              'forms',
              'components',
              'height',
              'ds-config-label-height'
            ],
            horizontalPadding: [
              'fields',
              'forms',
              'components',
              ...horizontalPaddingKeywords
            ]
          },
          subsection2: {
            textSize: ['fields', 'forms', 'components', ...fontSizeKeywords],
            fontWeight: [
              'fields',
              'forms',
              'components',
              ...fontWeightKeywords
            ],
            textColor: ['fields', 'forms', 'components', ...fontcolorKeywords]
          },
          subsection3: {
            verticalPadding: [
              'fields',
              'forms',
              'components',
              ...verticalPaddingKeywords
            ],
            borderWidth: [
              'fields',
              'forms',
              'components',
              'border',
              'width',
              'ds-config-label-border-width'
            ],
            borderColor: [
              'fields',
              'forms',
              'components',
              'border',
              'color',
              'ds-config-label-border-color'
            ]
          },
          prefixSuffix: {
            fontWeight: [
              'fields',
              'forms',
              'components',
              ...fontWeightKeywords,
              ...prefixSuffixGlobals
            ],
            backgroundOpacity: [
              'fields',
              'forms',
              'components',
              'background',
              'opacity',
              'ds-config-label-background-opacity',
              ...prefixSuffixGlobals
            ]
          }
        },
        buttons: {
          defaultTextSize: [
            'buttons',
            'default',
            'forms',
            'components',
            ...fontSizeKeywords,
            'ds-config-label-default-text-size'
          ],
          cornerRoundness: [
            'buttons',
            'forms',
            'components',
            ...borderRadiusKeywords
          ]
        },
        catalogs: {
          dropdownMaxHeight: [
            'dropdown',
            'max',
            'height',
            'ds-config-label-dropdown-max-height'
          ]
        },
        checkbox: {
          inlineHorizontalSeparation: [
            'forms',
            'components',
            'checkbox',
            'inline',
            'space',
            'separation',
            'distance',
            'ds-config-label-inline-horizontal-separation'
          ]
        },
        tabs: {
          subsection1: {
            borderWidth: [
              'forms',
              'components',
              'tabs',
              'border',
              'width',
              'ds-config-label-border-width'
            ],
            borderColor: [
              'forms',
              'components',
              'tabs',
              'border',
              'color',
              'ds-config-label-border-color'
            ]
          },
          tabPill: {
            column1: {
              containerPaddingTop: [
                'forms',
                'components',
                'tabs',
                'pill',
                'container',
                'padding',
                'distance',
                'space',
                'ds-config-label-container-padding'
              ],
              containerPaddingInline: [
                'forms',
                'components',
                'tabs',
                'pill',
                'container',
                'padding',
                'horizontal',
                'distance',
                'space',
                'ds-config-label-container-padding'
              ],
              containerBottomBorderWidth: [
                'forms',
                'components',
                'tabs',
                'pill',
                'container',
                'bottom',
                'border',
                'width',
                'ds-config-label-container-bottom-border-width'
              ]
            },
            column2: {
              horizontalPadding: [
                'forms',
                'components',
                'tabs',
                'pill',
                ...horizontalPaddingKeywords
              ],
              verticalPadding: [
                'forms',
                'components',
                'tabs',
                'pill',
                ...verticalPaddingKeywords
              ]
            },
            column3: {
              textSize: [
                'forms',
                'components',
                'tabs',
                'pill',
                ...fontSizeKeywords
              ],
              activeTabBgColor: [
                'forms',
                'components',
                'tabs',
                'pill',
                'active',
                'ds-config-label-active-tab-background-color',
                ...bgColorKeywords
              ]
            }
          }
        },
        blocks: {
          subsection1: {
            cardBgColor: [
              'forms',
              'components',
              'card',
              'blocks',
              'ds-config-label-card-background-color',
              ...bgColorKeywords
            ],
            bodyPadding: [
              'forms',
              'components',
              'body',
              'blocks',
              'padding',
              'distance',
              'ds-config-label-body-padding'
            ]
          },
          blockHeading: {
            column1: {
              textSize: [
                'forms',
                'components',
                'panel',
                'blocks',
                'heading',
                ...fontSizeKeywords
              ],
              fontWeight: [
                'forms',
                'components',
                'panel',
                'blocks',
                'heading',
                ...fontWeightKeywords
              ],
              textColor: [
                'forms',
                'components',
                'panel',
                'blocks',
                'heading',
                ...fontcolorKeywords
              ]
            },
            column2: {
              horizontalPadding: [
                'forms',
                'components',
                'panel',
                'blocks',
                'heading',
                ...horizontalPaddingKeywords
              ],
              verticalPadding: [
                'forms',
                'components',
                'panel',
                'blocks',
                'heading',
                ...verticalPaddingKeywords
              ]
            },
            column3: {
              borderColor: [
                'forms',
                'components',
                'panel',
                'blocks',
                'heading',
                'ds-config-label-border-color'
              ],
              backgroundColor: [
                'forms',
                'components',
                'panel',
                'blocks',
                'heading',
                ...bgColorKeywords
              ]
            }
          }
        },
        fieldset: {
          borderWidth: [
            'forms',
            'components',
            'fieldsets',
            'border',
            'width',
            'ds-config-label-border-width'
          ],
          legend: {
            textSize: [
              'forms',
              'components',
              'fieldsets',
              'legends',
              ...fontSizeKeywords
            ],
            fontWeight: [
              'forms',
              'components',
              'fieldsets',
              'legends',
              ...fontWeightKeywords
            ]
          }
        },
        content: {
          textLinesSeparation: [
            'forms',
            'components',
            'content',
            'text',
            'editor',
            'wysiwyg',
            'rich',
            'lines',
            'separation',
            'margin',
            'distance',
            'ds-config-label-text-lines-separation'
          ]
        },
        notices: {
          subsection1: {
            borderWidth: [
              'forms',
              'components',
              'notices',
              'border',
              'width',
              'message',
              'info',
              'warning',
              'alert',
              'ds-config-label-border-width'
            ],
            sideBorderWidth: [
              'forms',
              'components',
              'notices',
              'side',
              'border',
              'width',
              'message',
              'info',
              'warning',
              'alert',
              'ds-config-label-side-border-width'
            ]
          },
          subsection2: {
            cornerRoundness: [
              'forms',
              'components',
              'notices',
              'message',
              'info',
              'warning',
              'alert',
              ...borderRadiusKeywords
            ],
            horizontalPadding: [
              'forms',
              'components',
              'notices',
              'message',
              'info',
              'warning',
              'alert',
              ...horizontalPaddingKeywords
            ],
            verticalPadding: [
              'forms',
              'components',
              'notices',
              'message',
              'info',
              'warning',
              'alert',
              ...verticalPaddingKeywords
            ]
          }
        },
        datagrid: {
          subsection1: {
            borderWidth: [
              'forms',
              'components',
              'datagrid',
              'border',
              'width',
              'ds-config-label-border-width'
            ],
            cellBorderWidth: [
              'forms',
              'components',
              'datagrid',
              'cell',
              'border',
              'width',
              'ds-config-label-cell-border-width'
            ]
          },
          subsection2: {
            actionColumnWidth: [
              'forms',
              'components',
              'datagrid',
              'column',
              'action',
              'border',
              'width',
              'ds-config-label-action-column-width'
            ],
            cellHorizontalPadding: [
              'forms',
              'components',
              'datagrid',
              'cell',
              'ds-config-label-cell-horizontal-padding',
              ...horizontalPaddingKeywords
            ]
          }
        },
        file: {
          uploadIconSize: [
            'forms',
            'components',
            'files',
            'upload',
            'icon',
            'size',
            'cloud',
            'ds-config-label-upload-icon-size'
          ]
        },
        guide: {
          feesDocs: {
            itemSeparation: [
              'forms',
              'components',
              'guide',
              'fees',
              'documents',
              'blocks',
              'items',
              'separation',
              'distance',
              'space',
              'ds-config-label-item-separation'
            ],
            totalTextSize: [
              'forms',
              'components',
              'guide',
              'fees',
              'documents',
              'blocks',
              'counter',
              'totals',
              'ds-config-label-panel-total-text-size',
              ...fontSizeKeywords
            ],
            totalFontWeight: [
              'forms',
              'components',
              'guide',
              'fees',
              'documents',
              'blocks',
              'counter',
              'totals',
              'ds-config-label-panel-total-font-weight',
              ...fontWeightKeywords
            ]
          }
        },
        payment: {
          feesTotalTextSize: [
            'forms',
            'components',
            'payment',
            'page',
            'fees',
            'totals',
            'ds-config-label-fees-total-text-size',
            ...fontSizeKeywords
          ],
          feeSeparatorWidth: [
            'forms',
            'components',
            'payment',
            'page',
            'fees',
            'separator',
            'lines',
            'width',
            'ds-config-label-fees-separator-line-width'
          ],
          separatorColor: [
            'forms',
            'components',
            'payment',
            'page',
            'fees',
            'separator',
            'lines',
            'colors',
            'ds-config-label-separator-line-color'
          ]
        }
      }
    };
    this.searchResultMap = this.checkSearchResult(this.searchKeywordsMap) || {};
    Promise.all(this.translateKeywordsMapForLabel(this.searchKeywordsMap)).then(
      () => {
        if (this.searchTerm) {
          this.searchResultMap =
            this.checkSearchResult(this.searchKeywordsMap) || {};
        }
      }
    );
  }

  initRouteVisibilityConditionMap(): void {
    this.routeVisibilityConditionMap = {
      system: {
        dashboard: [/^\/$/],
        availableServices: [/^\/$/]
      },
      forms: ['/services']
    };
    this.routeVisibilityMap =
      this.checkRouteVisibility(this.routeVisibilityConditionMap) || {};
  }

  translateKeywordsMapForLabel(keywordsMap: any): any[] {
    const pending: any[] = [];
    if (Array.isArray(keywordsMap)) {
      const localize = $localize;
      for (let i = 0; i < keywordsMap.length; i++) {
        /* eslint-disable security/detect-object-injection -- array index iteration */
        if (keywordsMap[i].startsWith('ds-config-label-')) {
          const id = keywordsMap[i];
          pending.push(localize(<any>{ '0': `:@@${id}:${id}`, raw: [':'] }));
          pending.push($localize`${keywordsMap[i]}`);
          /* eslint-enable security/detect-object-injection */
        }
      }
    } else if (keywordsMap && typeof keywordsMap === 'object') {
      for (const key of Object.keys(keywordsMap)) {
        // eslint-disable-next-line security/detect-object-injection -- key from Object.keys() iteration
        pending.push(...this.translateKeywordsMapForLabel(keywordsMap[key]));
      }
    }

    return pending;
  }

  onChangeSearchTerm() {
    const searchTerm = this.searchTermText.trim().toLowerCase();
    if (this.searchTerm === searchTerm) {
      return;
    }
    this.searchTerm = searchTerm;
    this.searchTermWords = searchTerm.split(/\s+/);
    this.searchResultMap = this.checkSearchResult(this.searchKeywordsMap) || {};
    this.searchTerm = searchTerm;
  }

  checkSearchResult(keywordsMap: any) {
    if (Array.isArray(keywordsMap)) {
      return (
        !this.searchTerm ||
        keywordsMap.some((x) =>
          this.searchTermWords.every((y) => x.includes(y))
        )
      );
    } else if (keywordsMap && typeof keywordsMap === 'object') {
      const result: any = {};
      for (const key of Object.keys(keywordsMap)) {
        // eslint-disable-next-line security/detect-object-injection -- key from Object.keys() iteration
        const r = this.checkSearchResult(keywordsMap[key]);
        if (r) {
          // eslint-disable-next-line security/detect-object-injection -- key from Object.keys() iteration
          result[key] = r;
        }
      }
      if (Object.keys(result).length > 0) {
        return result;
      }
    }
    return null;
  }

  checkRouteVisibility(routeMap: any) {
    if (Array.isArray(routeMap)) {
      return (
        this.advancedFlag ||
        routeMap.some((x) => {
          if (x instanceof RegExp) {
            return x.exec(this.router.url) !== null;
          }
          return this.router.url.startsWith(x);
        })
      );
    } else if (routeMap && typeof routeMap === 'object') {
      const result: any = {};
      for (const key of Object.keys(routeMap)) {
        // eslint-disable-next-line security/detect-object-injection -- key from Object.keys() iteration
        const r = this.checkRouteVisibility(routeMap[key]);
        if (r) {
          // eslint-disable-next-line security/detect-object-injection -- key from Object.keys() iteration
          result[key] = r;
        }
      }
      if (Object.keys(result).length > 0) {
        return result;
      }
    }
    return null;
  }

  toggleTab(tab: string) {
    this.activeTab = tab;
  }

  toggleAdvanced(): void {
    this.advancedFlag = !this.advancedFlag;
    this.routeVisibilityMap =
      this.checkRouteVisibility(this.routeVisibilityConditionMap) || {};
    this.checkActiveTab();
  }

  checkActiveTab(): void {
    const system = this.routeVisibilityMap.system;
    const forms = this.routeVisibilityMap.forms;
    let activeTab = 'system';

    switch (this.activeTab) {
      case 'system': {
        if (system) {
          break;
        }
        break;
      }
      case 'forms': {
        if (forms) {
          activeTab = 'forms';
        }
        break;
      }
    }
    this.activeTab = activeTab;
  }

  toggleClass(event: Event, className: string): void {
    const target = event.currentTarget as HTMLElement | null;
    const fieldset = target?.closest('fieldset');

    if (!fieldset) {
      return;
    }

    if (fieldset.classList.contains(className)) {
      this.renderer.removeClass(fieldset, className);
    } else {
      this.renderer.addClass(fieldset, className);
    }
  }
}
